/**
 * Этажные плечи ВЕДЕНИЯ — «этажный замок» (23.09.2026).
 *
 * Полилиния маршрута плоская: вершины второго этажа лежат в тех же координатах,
 * что и первого. Ведение, искавшее ближайшую вершину по всему маршруту, у входа
 * под целью на 2-м этаже «поднимало» человека наверх и объявляло прибытие
 * (баг владельца 23.09). Отраслевой ответ (Situm: шаг «change floor»; научный
 * multi-floor map matching; HMM Newson–Krumm): между этажами ходят только через
 * переходы — лестницу, эскалатор, лифт, — и смену этажа подтверждает датчик
 * или человек, а не близость по горизонтали.
 *
 * Здесь — чистая математика: какие вершины `plan.polyline` лежат на каком
 * этаже. Соседние плечи делят вершину перехода (низ лестницы): отрезок самой
 * лестницы принадлежит плечу этажа, НА который поднимаются. Ведение матчит
 * позицию только внутри текущего плеча (nav/session).
 *
 * От `route-level-legs.ts` (отрисовка) отличается тем, что работает в индексах
 * денсифицированной полилинии и склеивает «сквозные» этажи: лифт с 1-го на
 * 3-й — один переход, а не два с подтверждением на 2-м.
 */
import type { LonLat } from '@app/geo'
import { haversineM } from '@app/geo'

/** Как попали на плечо с предыдущего. */
export interface RouteFloorEntry {
  /** Уровень, с которого пришли. */
  readonly fromLevel: string
  /** Чем переходят: stairs | escalator | elevator | … (тип узла-перехода). */
  readonly kind: string
  /** true — вверх (ordinal растёт). */
  readonly up: boolean
}

export interface RouteFloorLeg {
  /** Уровень плеча; null — у маршрута без этажей (улица, промзона). */
  readonly level: string | null
  /** Индексы вершин plan.polyline, включительно; соседи делят вершину перехода. */
  readonly startIndex: number
  readonly endIndex: number
  /** null у первого плеча. */
  readonly entry: RouteFloorEntry | null
}

/** Опорная точка маршрута ДО денсификации: точка, уровень и тип узла. */
export interface LeveledPoint {
  readonly point: LonLat
  /** null — уровень не задан: наследуется от предыдущей точки. */
  readonly level: string | null
  readonly kind: string
}

export interface FloorLegOpts {
  /** Порог склейки соседних точек, которым планировщик дедупил сырьё. */
  readonly dupEpsM: number
  /** ordinal уровня; undefined — уровень неизвестен площадке. */
  readonly ordinalOf: (levelId: string) => number | undefined
}

/** Промежуточный этаж короче этого — площадка лестницы или лифт «насквозь». */
export const FLOOR_PASS_THROUGH_M = 8

interface Mark {
  readonly index: number
  readonly level: string | null
  readonly kind: string
}

interface MutLeg {
  level: string | null
  startIndex: number
  endIndex: number
  entry: RouteFloorEntry | null
}

/**
 * Этажные плечи для полилинии `densify(dedupe(points))`. Точки ищутся в
 * полилинии по точному совпадению (densify сохраняет исходные вершины);
 * склеенная дедупом точка получает индекс предыдущей сохранённой.
 */
export function buildFloorLegs(
  polyline: readonly LonLat[],
  points: readonly LeveledPoint[],
  opts: FloorLegOpts,
): RouteFloorLeg[] {
  const last = polyline.length - 1
  if (last < 0) return []
  const marks = markIndices(polyline, points, opts.dupEpsM)
  const legs: MutLeg[] = [{ level: null, startIndex: 0, endIndex: last, entry: null }]
  let prevIndex = 0
  let prevKind = ''
  for (const m of marks) {
    const cur = legs[legs.length - 1] as MutLeg
    if (m.level !== null && cur.level === null) {
      // Улица → здание: уровень появился, но этаж никто не менял.
      cur.level = m.level
    } else if (m.level !== null && cur.level !== null && differ(cur.level, m.level, opts)) {
      cur.endIndex = prevIndex
      legs.push({
        level: m.level,
        startIndex: prevIndex,
        endIndex: last,
        entry: {
          fromLevel: cur.level,
          // Узел-переход и есть лестница/лифт; сосед-коридор вида не несёт.
          kind: m.kind !== 'junction' ? m.kind : prevKind || m.kind,
          up: isUp(cur.level, m.level, opts),
        },
      })
    }
    prevIndex = m.index
    prevKind = m.kind
  }
  return mergePassThrough(polyline, legs, opts)
}

/** Индекс каждой опорной точки в полилинии; уровень без значения — наследуется. */
function markIndices(
  polyline: readonly LonLat[],
  points: readonly LeveledPoint[],
  dupEpsM: number,
): Mark[] {
  const marks: Mark[] = []
  let j = 0
  let level: string | null = null
  for (const p of points) {
    level = p.level ?? level
    const kept = polyline[j]
    if (marks.length > 0 && kept && haversineM(kept, p.point) < dupEpsM) {
      marks.push({ index: j, level, kind: p.kind })
      continue
    }
    let k = j
    while (k < polyline.length && !samePoint(polyline[k], p.point)) k++
    if (k < polyline.length) j = k
    marks.push({ index: j, level, kind: p.kind })
  }
  return marks
}

/**
 * Короткое промежуточное плечо (лифт 1→3 стоит на 2-м долю метра) сливается
 * в переход; вернулись на тот же этаж — перехода не было.
 */
function mergePassThrough(
  polyline: readonly LonLat[],
  legs: readonly MutLeg[],
  opts: FloorLegOpts,
): RouteFloorLeg[] {
  const out: MutLeg[] = []
  for (const next of legs) {
    let leg: MutLeg = { ...next }
    const short = out[out.length - 1]
    if (
      out.length >= 2 &&
      short?.entry &&
      leg.entry &&
      pathLengthM(polyline, short.startIndex, short.endIndex) < FLOOR_PASS_THROUGH_M
    ) {
      out.pop()
      const from = short.entry.fromLevel
      leg = {
        ...leg,
        startIndex: short.startIndex,
        entry: { fromLevel: from, kind: short.entry.kind, up: isUp(from, leg.level ?? from, opts) },
      }
    }
    const before = out[out.length - 1]
    if (before && leg.entry && !differ(before.level ?? '', leg.level ?? '', opts)) {
      before.endIndex = leg.endIndex
      continue
    }
    out.push(leg)
  }
  return out
}

/**
 * Плечи обратного пути (тот же маршрут задом наперёд): индексы зеркалятся,
 * переход ведёт на этаж, с которого раньше пришли, в обратную сторону.
 */
export function reverseFloorLegs(
  legs: readonly RouteFloorLeg[],
  polylineLength: number,
): RouteFloorLeg[] {
  const n = polylineLength - 1
  const out: RouteFloorLeg[] = []
  for (let m = legs.length - 1; m >= 0; m--) {
    const leg = legs[m] as RouteFloorLeg
    const after = legs[m + 1]
    out.push({
      level: leg.level,
      startIndex: n - leg.endIndex,
      endIndex: n - leg.startIndex,
      entry:
        after?.entry && after.level !== null
          ? { fromLevel: after.level, kind: after.entry.kind, up: !after.entry.up }
          : null,
    })
  }
  return out
}

/**
 * Плечи склеенного маршрута (тур по магазинам: полилинии стопов подряд).
 * Стык на том же этаже — одно плечо; на другом — переход без вида.
 */
export function concatFloorLegs(
  parts: readonly { readonly polylineLength: number; readonly legs: readonly RouteFloorLeg[] }[],
  ordinalOf: (levelId: string) => number | undefined = () => undefined,
): RouteFloorLeg[] {
  const out: MutLeg[] = []
  let offset = 0
  for (const part of parts) {
    for (const [i, leg] of part.legs.entries()) {
      const shifted: MutLeg = {
        ...leg,
        startIndex: leg.startIndex + offset,
        endIndex: leg.endIndex + offset,
      }
      const prev = out[out.length - 1]
      if (i === 0 && prev && (prev.level === leg.level || leg.level === null)) {
        prev.endIndex = shifted.endIndex
        continue
      }
      if (i === 0 && prev?.level) {
        shifted.startIndex = prev.endIndex
        shifted.entry = {
          fromLevel: prev.level,
          kind: '',
          up: isUp(prev.level, leg.level ?? prev.level, { dupEpsM: 0, ordinalOf }),
        }
      }
      out.push(shifted)
    }
    offset += part.polylineLength
  }
  return out
}

/**
 * Этажи разные — по ordinal: другое здание на том же этаже (или улица и
 * первый этаж) — не переход. Уровень без ordinal не запирает ведение: ложный
 * замок у двери хуже старого поведения.
 */
function differ(a: string, b: string, opts: FloorLegOpts): boolean {
  const oa = opts.ordinalOf(a)
  const ob = opts.ordinalOf(b)
  return oa !== undefined && ob !== undefined && oa !== ob
}

function isUp(from: string, to: string, opts: FloorLegOpts): boolean {
  return (opts.ordinalOf(to) ?? 0) > (opts.ordinalOf(from) ?? 0)
}

function samePoint(a: LonLat | undefined, b: LonLat): boolean {
  return a !== undefined && a[0] === b[0] && a[1] === b[1]
}

function pathLengthM(polyline: readonly LonLat[], from: number, to: number): number {
  let sum = 0
  for (let i = from; i < to; i++) {
    const a = polyline[i]
    const b = polyline[i + 1]
    if (a && b) sum += haversineM(a, b)
  }
  return sum
}
